import { useState } from 'react';

import { Flex, VFlex } from '@/components/base';
import Button from '@/components/Button/Button';
import Typo from '@/components/Typography/Typo';
import useColor from '@/hooks/domain/useColor';

import FilterItemEmail from './FilterItemEmail';

type FilterItemsEmailModalProps = {
  existingEmails: string[];
  existingEmailDomains: string[];
  onConfirm: (type: 'email' | 'domain', value: string) => void;
  editDisabled?: boolean;
};

enum ErrorFilterEmail {
  ErrDuplicated = 'Same email already exists',
  ErrFormatInvalid = 'Invalid email format',
}

enum ErrorFilterEmailDomain {
  ErrDuplicated = 'Same email domain already exists',
  ErrFormatInvalid = 'Invalid email domain format',
}

const FilterItemsAddEmail = (props: FilterItemsEmailModalProps) => {
  const color = useColor();
  const [newEmailFilter, setNewEmailFilter] = useState('');
  const [newDomainFilter, setNewDomainFilter] = useState('');

  const [newEmailFilterError, setNewEmailFilterError] =
    useState<ErrorFilterEmail | null>(null);
  const [newDomainFilterError, setNewDomainFilterError] =
    useState<ErrorFilterEmailDomain | null>(null);

  const validateEmailFormat = (email: string) => {
    const emailRegex: RegExp =
      /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

    if (!emailRegex.test(email)) {
      return ErrorFilterEmail.ErrFormatInvalid;
    }

    const decapitalizedEmail = email.toLowerCase();

    if (
      props.existingEmails
        .map((email) => email.toLowerCase())
        .includes(decapitalizedEmail)
    ) {
      return ErrorFilterEmail.ErrDuplicated;
    }

    return undefined;
  };

  const validateEmailDomainFormat = (emailDomain: string) => {
    const domainRegex: RegExp = /^[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

    if (!domainRegex.test(emailDomain)) {
      return ErrorFilterEmailDomain.ErrFormatInvalid;
    }

    const decapitalizedEmailDomain = emailDomain.toLowerCase();

    if (
      props.existingEmailDomains
        .map((emailDomain) => emailDomain.toLowerCase())
        .includes(decapitalizedEmailDomain)
    ) {
      return ErrorFilterEmailDomain.ErrDuplicated;
    }

    return undefined;
  };

  const handleChangeNewEmailFilter = (value: string) => {
    const error = validateEmailFormat(value);

    setNewEmailFilter(value);

    if (error) {
      setNewEmailFilterError(error);
    } else {
      setNewEmailFilterError(null);
    }
  };

  const handleChangeNewDomainFilter = (value: string) => {
    const error = validateEmailDomainFormat(value);

    setNewDomainFilter(value);

    if (error) {
      setNewDomainFilterError(error);
    } else {
      setNewDomainFilterError(null);
    }
  };

  const addEmailFilterDisabled = Boolean(
    !newEmailFilter || props.editDisabled || newEmailFilterError,
  );
  const addEmailDomainFilterDisabled = Boolean(
    !newDomainFilter || props.editDisabled || newDomainFilterError,
  );

  const initNewEmailFilter = () => {
    setNewEmailFilter('');
    setNewEmailFilterError(null);
  };

  const initNewDomainFilter = () => {
    setNewDomainFilter('');
    setNewDomainFilterError(null);
  };

  const handleConfirmNewEmailFilter = () => {
    if (addEmailFilterDisabled) {
      return;
    }

    props.onConfirm('email', newEmailFilter);
    initNewEmailFilter();
  };

  const handleConfirmNewDomainFilter = () => {
    if (addEmailDomainFilterDisabled) {
      return;
    }

    props.onConfirm('domain', newDomainFilter);
    initNewDomainFilter();
  };

  return (
    <>
      <Flex g={24} a="flex-start">
        <VFlex g={6} f={1}>
          <FilterItemEmail
            initFocus
            readOnly={props.editDisabled}
            text={newEmailFilter}
            onChange={handleChangeNewEmailFilter}
            actionButton={
              <Button
                variant="ghost"
                sizeType="m"
                onClick={handleConfirmNewEmailFilter}
                disabled={addEmailFilterDisabled}
              >
                Add
              </Button>
            }
            placeholder="name@example.com"
          />
          {newEmailFilterError && (
            <Typo weight="m" color={color.red600}>
              {newEmailFilterError}
            </Typo>
          )}
        </VFlex>

        <VFlex g={6} f={1}>
          <FilterItemEmail
            readOnly={props.editDisabled}
            text={newDomainFilter}
            onChange={handleChangeNewDomainFilter}
            actionButton={
              <Button
                variant="ghost"
                sizeType="m"
                onClick={handleConfirmNewDomainFilter}
                disabled={addEmailDomainFilterDisabled}
              >
                Add
              </Button>
            }
            placeholder="example.com"
          />
          {newDomainFilterError && (
            <Typo weight="m" color={color.red600}>
              {newDomainFilterError}
            </Typo>
          )}
        </VFlex>
      </Flex>
    </>
  );
};

export default FilterItemsAddEmail;
